import * as React from "react";
import "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import DateRangeIcon from "@mui/icons-material/DateRange";
import { date } from "../refdata/constants";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Form, KeyValue } from "../interfaces";
import clsx from "clsx";
import { DesktopDatePicker, DateValidationError } from "@mui/x-date-pickers";
import { messages } from "../refdata/UserMessages";

export interface Props {
  fieldName: string;
  label: string;
  currentValue: string;
  minDateValue: string;
  maxDateValue: string;
  form: Form;
  setForm: (object: any) => void;
  errors: KeyValue;
  id?: string;
  disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
  disabled: {
    "& *, & .MuiFormLabel-root.Mui-disabled": {
      background: "none",
      color: theme.palette.primary.main,
    },
  },
  datePicker: {
    "marginTop": 0,
    "marginBottom": 16,
    "width": "100%",
    "& input": {
      padding: "11.5px 14px",
    },
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: 8,
    },
    "& button": {
      padding: 5,
    },
  },
  datePickerModal: {
    "& h4": {
      paddingBottom: 0,
    },
    "& h6.MuiTypography-subtitle1": {
      "fontSize": "1.2rem",
      "paddingTop": 0,
      "marginTop": 0,
      "color": "rgba(255, 255, 255, 0.75)",
      "&::after": {
        content: '" ▾"',
      },
    },
    "& h6.MuiPickersToolbarText-toolbarBtnSelected": {
      color: "#fff",
    },
    "& .MuiPickersToolbarButton-toolbarBtn": {
      "padding": "0 5px",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.3) !important",
      },
    },
    "& p": {
      padding: "unset",
    },
    "& .MuiToolbar-root .Mui-focusVisible": {
      backgroundColor: theme.palette.primary.dark,
    },
    "& .Mui-focusVisible": {
      backgroundColor: theme.palette.secondary.main,
      color: "#000",
    },
  },
}));

export function DateFilterField({
  fieldName,
  label,
  setForm,
  form,
  errors,
  currentValue,
  minDateValue,
  maxDateValue,
  id,
  disabled,
}: Props) {
  const classes = useStyles(useTheme);
  const errorMessageLookup: KeyValue = {
    //financials inactive dates
    inactiveStartRangemaxDate: messages.datePicker.startDateMaxEnd,
    inactiveStartRangeminDate: messages.datePicker.inactiveStartDateMin,
    inactiveEndRangeminDate:
      new Date(currentValue) < new Date(date.inactiveMin)
        ? messages.datePicker.inactiveStartDateMin
        : messages.datePicker.endDateMinStart,
    inactiveStartRangeinvalidDate: messages.datePicker.inactiveStartDateMin,
    inactiveEndRangeinvalidDate: messages.datePicker.inactiveEndDateMinDefault,
    inactiveStartRangedisableFuture: messages.datePicker.startDateMaxCurrent,
    inactiveEndRangedisableFuture: messages.datePicker.endDateMax,
    //financials established dates
    establishedStartRangemaxDate: messages.datePicker.startDateMaxEnd,
    establishedStartRangeminDate: messages.datePicker.financialStartDateMin,
    establishedEndRangeminDate:
      new Date(currentValue) < new Date(date.financialMin)
        ? messages.datePicker.financialStartDateMin
        : messages.datePicker.endDateMinStart,
    establishedStartRangeinvalidDate: messages.datePicker.financialStartDateMin,
    establishedEndRangeinvalidDate:
      messages.datePicker.financialEndDateMinDefault,
    establishedStartRangedisableFuture: messages.datePicker.startDateMaxCurrent,
    establishedEndRangedisableFuture: messages.datePicker.endDateMax,
    //structure change dates
    startDatemaxDate: messages.datePicker.startDateMaxEnd,
    startDateminDate: messages.datePicker.startDateMin,
    endDateminDate:
      new Date(currentValue) < new Date(date.defaultMin)
        ? messages.datePicker.endDateMinDefault
        : messages.datePicker.endDateMinStart,
    startDateinvalidDate: messages.datePicker.startDateMin,
    endDateinvalidDate: messages.datePicker.endDateMinDefault,
    startDatedisableFuture: messages.datePicker.startDateMaxCurrent,
    endDatedisableFuture: messages.datePicker.endDateMax,
  };

  const [errorMessage, setErrorMessage] = React.useState("");
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        value={dayjs(currentValue)}
        minDate={dayjs(minDateValue)}
        maxDate={dayjs(maxDateValue)}
        label={label}
        disabled={disabled}
        disableFuture
        className={clsx(classes.datePicker, disabled && classes.disabled)}
        onChange={newValue => {
          setForm({
            ...form,
            [fieldName]: newValue ? newValue.toDate().toLocaleDateString() : "",
          });
        }}
        slots={{
          openPickerIcon: DateRangeIcon,
        }}
        slotProps={{
          openPickerButton: {
            color: "primary",
          },
          textField: {
            id: id
              ? id
              : fieldName === date.startDate
              ? date.startDateId
              : date.endDateId,

            helperText: errorMessageLookup[errorMessage]
              ? errorMessageLookup[errorMessage]
              : errorMessage,
          },
        }}
        onError={(error: DateValidationError) => {
          if (error) {
            setErrorMessage(`${fieldName}${error}`);
            errors[fieldName] = error;
          } else {
            setErrorMessage("");
            delete errors[fieldName];
          }
        }}
      />
    </LocalizationProvider>
  );
}
